import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { ApplicationState } from "../../store";
import { LoginOptionState } from "../../store/reducers/login-option-reducer";
import { SP_SPDL_TICKET_NUMBER } from "../../constants/ticketNumberRegex";

interface Props {
  children?: React.ReactNode;
}

const ScrollToTop: React.FC<Props> = (props) => {
  const location = useLocation();
  const history = useHistory();
  const prevPathnameRef = useRef<string>("");

  const { isLoginModalOpen } = useSelector<ApplicationState, LoginOptionState>(
    (state) => state.loginOption
  );

  /**
   * Extracts the base route by removing ticket number from the pathname.
   * This allows comparing routes without modal-specific identifiers.
   * Examples:
   * - "/my-data/SP-123" -> "/my-data"
   * - "/my-data/history/SPDL-456" -> "/my-data/history"
   * - "/my-data" -> "/my-data"
   */
  const getBaseRoute = (pathname: string): string => {
    const pathSegments = pathname.split("/").filter(Boolean);
    const lastSegment = pathSegments[pathSegments.length - 1];

    if (lastSegment && SP_SPDL_TICKET_NUMBER.test(lastSegment)) {
      pathSegments.pop();
    }

    return "/" + pathSegments.join("/");
  };

  useEffect(() => {
    const prevBaseRoute = getBaseRoute(prevPathnameRef.current);
    const currentBaseRoute = getBaseRoute(location.pathname);

    // Only scroll if base route changed (actual page navigation, not modal open/close)
    if (
      history.action !== "REPLACE" &&
      !isLoginModalOpen &&
      prevBaseRoute !== currentBaseRoute
    ) {
      const main = document.querySelector("main");
      if (main) {
        main.scrollTo(0, 0);
      }
      window.scrollTo(0, 0);
    }

    // Update ref for next navigation
    prevPathnameRef.current = location.pathname;
  }, [location, history]);

  return <>{props.children}</>;
};

export default ScrollToTop;
